import React, { useState } from 'react';
import './Banner.css';

export type BannerVariant = 'info' | 'warning' | 'error' | 'success';

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant */
  variant?: BannerVariant;
  /** Banner content */
  children: React.ReactNode;
  /** Whether the banner can be dismissed */
  dismissible?: boolean;
  /** Called when the banner is dismissed */
  onDismiss?: () => void;
  /** Optional icon displayed before the content */
  icon?: React.ReactNode;
}

export const Banner: React.FC<BannerProps> = ({
  variant = 'info',
  children,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
  ...rest
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const classes = [
    'hi-ui-banner',
    `hi-ui-banner--${variant}`,
    icon ? 'hi-ui-banner--with-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div className={classes} role="alert" {...rest}>
      {icon && (
        <span className="hi-ui-banner__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <div className="hi-ui-banner__content">{children}</div>
      {dismissible && (
        <button
          type="button"
          className="hi-ui-banner__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M4.47 4.47a.75.75 0 011.06 0L8 6.94l2.47-2.47a.75.75 0 111.06 1.06L9.06 8l2.47 2.47a.75.75 0 11-1.06 1.06L8 9.06l-2.47 2.47a.75.75 0 01-1.06-1.06L6.94 8 4.47 5.53a.75.75 0 010-1.06z" />
          </svg>
        </button>
      )}
    </div>
  );
};
